import { Assets } from '@core/GUI/Utils/Assets';
import { BetsDisplay } from '@core/GUI/BetsDisplay';
import { Constants, effectsVolume, virtualWheel } from '@core/Utils/Constants';
import { BettableButton } from '@core/GUI/BettableButton';
import { HotSpot } from '@core/GUI/HotSpot';
import { Phase, BET_CHECKING_MODE } from '@core/Logic/Logic';
import { MiscButton } from '@core/GUI/MiscButton';
import { Language, Messages } from '@core/GUI/Language';
import { RepeatBets, UndoBet } from '@core/Utils/ButtonActions';
import { ButtonAction } from 'BaseRoulette/Sources/GUI/LayoutSwitchButton';
import { StatsHolder } from '@core/GUI/StatsHolder';
import { Client } from '@core/Client';
import { numToColor } from '@core/Utils/HelperFunction';
import { Button } from '@core/GUI/Button';

export class Board extends Phaser.GameObjects.Container {
	public statisticsAreDisplayed: boolean = false;
	public boardButtons = new Array<BettableButton>()
	public winAnimation: Array<Phaser.Tweens.Tween> = new Array<Phaser.Tweens.Tween>();
	public betsDisplay: BetsDisplay;

	protected hotSpotsTable: HotSpot[][];
	protected boardButtonsDictionary = new Array<BettableButton>();
	protected statisticsBars: Array<StatsHolder>;
	protected numberStatistics: any[];
	protected isBoardEmpty: boolean = true;
	protected inputRect: Phaser.GameObjects.Rectangle;
	protected repeatBtn: MiscButton;
	protected switchLayoutBtn: MiscButton | Button;
	protected undoBtn: MiscButton;
	protected presetBtn: MiscButton;

	constructor(scene: Phaser.Scene, protected textures: Phaser.Textures.TextureManager) {
		super(scene);

		this.createBoardButtons();
		
		this.add(this.boardButtons);
		this.createHotSpots();

		this.betsDisplay = new BetsDisplay(this, this.scene, this.hotSpotsTable, new Assets());
		this.betsDisplay.RefreshChips();

		// on recovery Board is created too slowly to display wins right away!
		if (Client.logic.CurrentWinNumber != -1 && Client.logic.Phase == Phase.READING_WIN_NUMBER_STATE)
			this.displayWins(Client.logic.CurrentWinNumber);

		this.scene.game.events.on(Constants.EVENTS.WIN_NUMBER, (winNumber: number) => { this.displayWins(winNumber); });
		this.scene.game.events.on(Constants.EVENTS.PHASE_EVENT, (phase: number) => { this.OnPhaseEvent(phase); });
		this.scene.game.events.on(Constants.EVENTS.TOGGLE_BOARD_HOTSPOTS, (hotSpots: Array<number>, highlighted: boolean) => { this.ToggleButtonsHighLight(hotSpots, highlighted) });
		this.scene.game.events.on(Constants.EVENTS.TOGGLE_BOARD_HOTSPOTS_EFFECTS, (enabled: boolean) => { this.ToggleHotSpotsEffects(enabled) });
		this.scene.game.events.on(Constants.EVENTS.BETS_OPENED, () => { this.displayWins(-1); });

		this.inputRect = new Phaser.GameObjects.Rectangle(this.scene, 0, 0, this.width, this.height);
		this.inputRect.setOrigin(0);
		this.inputRect.on("pointerout", () => {
			this.betsDisplay.ToggleTouchPointer(false);
		});
		this.add(this.inputRect);
	}

	Update(time: number) {
		if (this.betsDisplay)
			this.betsDisplay.Update(time);
		if (this.isBoardEmpty != Client.logic.IsEmpty)
			this.OnEmptyChanged(Client.logic.IsEmpty);
	}

	protected createBoardButtons(): void {
		this.boardButtons = new Array<BettableButton>(37);
		if (Client.logic.IsDoubleZero()) {
			let zero = this.textures.get(Constants.SPRITESHEET).frames[Assets.Board.Buttons.Board_Button_Zero.Double_Zero_0.Normal];
			let zeroButton = new BettableButton("0", Constants.SPRITESHEET, this.scene, 0, 0, zero.width, zero.height, Assets.Board.Buttons.Board_Button_Zero.Double_Zero_0.Normal, Assets.Board.Buttons.Board_Button_Zero.Double_Zero_0.Hover, Assets.Board.Buttons.Board_Button_Zero.Double_Zero_0.Win);
			zeroButton.bettableButtonBackground.setOrigin(0, 0)
			this.boardButtonsDictionary["0"] = zeroButton
			this.boardButtons[0] = zeroButton

			let doubleZero = this.textures.get(Constants.SPRITESHEET).frames[Assets.Board.Buttons.Board_Button_Zero.Double_Zero_00.Normal];
			let doubleZeroButton = new BettableButton("37", Constants.SPRITESHEET, this.scene, 0, 0, doubleZero.width, doubleZero.height, Assets.Board.Buttons.Board_Button_Zero.Double_Zero_00.Normal, Assets.Board.Buttons.Board_Button_Zero.Double_Zero_00.Hover, Assets.Board.Buttons.Board_Button_Zero.Double_Zero_00.Win);
			doubleZeroButton.bettableButtonBackground.setOrigin(0, 0)
			this.boardButtonsDictionary["37"] = doubleZeroButton
			this.boardButtons[37] = doubleZeroButton
		} else {
			let zero = this.textures.get(Constants.SPRITESHEET).frames[Assets.Board.Buttons.Board_Button_Zero.Single_Zero.Normal];
			let zeroButton = new BettableButton("0", Constants.SPRITESHEET, this.scene, 0, 0, zero.width, zero.height, Assets.Board.Buttons.Board_Button_Zero.Single_Zero.Normal, Assets.Board.Buttons.Board_Button_Zero.Single_Zero.Hover, Assets.Board.Buttons.Board_Button_Zero.Single_Zero.Win);
			zeroButton.bettableButtonBackground.setOrigin(0, 0)
			this.boardButtonsDictionary["0"] = zeroButton
			this.boardButtons[0] = zeroButton;
		}

		let num = 0
		let buttonTexture = this.textures.get(Constants.SPRITESHEET).frames["board_button_1.png"];
		for (let i = 0; i < 12; i++) {
			for (let j = 3; j > 0; j--) {
				num++;
				let n = num + "";
				let t = "board_button_" + n + ".png";
				let a = "board_button_" + n + "_hover.png";
				let w = "board_button_" + n + "_win.png";
				let b = new BettableButton(n, Constants.SPRITESHEET, this.scene, 0, 0, buttonTexture.width, buttonTexture.height, t, a, w)
				b.bettableButtonBackground.setOrigin(0, 0);
				this.boardButtonsDictionary[n] = b;
				this.boardButtons[n] = b;
			}
		}

		let names = [Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_THIRD, Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_THIRD, Constants.BOARD.BOARD_BUTTON_NAMES.THIRD_THIRD, Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_HALF, Constants.BOARD.BOARD_BUTTON_NAMES.EVEN, Constants.BOARD.BOARD_BUTTON_NAMES.RED, Constants.BOARD.BOARD_BUTTON_NAMES.BLACK, Constants.BOARD.BOARD_BUTTON_NAMES.ODD, Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_HALF, Constants.BOARD.BOARD_BUTTON_NAMES.TWELVE_ONE, Constants.BOARD.BOARD_BUTTON_NAMES.TWENTY_ONE_ONE, Constants.BOARD.BOARD_BUTTON_NAMES.THIRTY_TWO_ONE]
		for (let i = 0; i < names.length; i++) {
			let d = this.textures.get(Constants.SPRITESHEET).frames["board_button_" + names[i] + ".png"];
			let n = "board_button_" + names[i] + ".png";
			let h = "board_button_" + names[i] + "_hover.png";
			let w = "board_button_" + names[i] + "_win.png";
			let g = new BettableButton(names[i], Constants.SPRITESHEET, this.scene, 0, 0, d.width, d.height, n, h, w);
			g.bettableButtonBackground.setOrigin(0);
			this.boardButtonsDictionary[names[i]] = g;
			this.boardButtons.push(g);
		}

		if (Client.logic.IsDoubleZero())
			this.boardButtons.push(this.boardButtonsDictionary["37"]);

		this.CreateSideButtons();
	}

	protected CreateSideButtons(): void {
		this.switchLayoutBtn = new MiscButton(this.scene, -1, Constants.SPRITESHEET, Assets.Board.Buttons.Track_Switch.Normal, Assets.Board.Buttons.Track_Switch.Disabled, Assets.Board.Buttons.Track_Switch.Hover, new ToggleLayout(), false, Messages.TRACK_LAYOUT)
		this.switchLayoutBtn.name = Constants.BOARD.MISC_BUTTON_NAMES.SWITCH_LAYOUT
		this.add(this.switchLayoutBtn);
		this.add(this.switchLayoutBtn.TextElement);

		this.repeatBtn = new MiscButton(this.scene, -1, Constants.SPRITESHEET, Assets.Board.Buttons.Repeat_Bets.Normal, Assets.Board.Buttons.Repeat_Bets.Disabled, Assets.Board.Buttons.Repeat_Bets.Hover, new RepeatBets(), false, Messages.REPEAT)
		this.repeatBtn.name = Constants.BOARD.MISC_BUTTON_NAMES.REPEAT_BETS
		this.add(this.repeatBtn);
		this.add(this.repeatBtn.TextElement);

		this.undoBtn = new MiscButton(this.scene, -1, Constants.SPRITESHEET, Assets.Board.Buttons.Undo_Bet.Normal, Assets.Board.Buttons.Undo_Bet.Disabled, Assets.Board.Buttons.Undo_Bet.Hover, new UndoBet(), false, Messages.UNDO)
		this.undoBtn.name = Constants.BOARD.MISC_BUTTON_NAMES.UNDO_BET
		this.add(this.undoBtn);
		this.add(this.undoBtn.TextElement);

		this.presetBtn = new MiscButton(this.scene, -1, Constants.SPRITESHEET, Assets.PresetPanel.Buttons.Preset_Switch.Normal, Assets.PresetPanel.Buttons.Preset_Switch.Disabled, Assets.PresetPanel.Buttons.Preset_Switch.Hover, new TogglePresets(this.scene), false, Messages.PRESETS)
		this.presetBtn.name = "PRESETS_BUTTON"
		this.add(this.presetBtn);
		this.add(this.presetBtn.TextElement);
	}

	protected positionBoardButtons(viewportWidth: number, viewportHeight: number, portraitMode, staticW, staticH): void {
		let sourceButtonDimensions = { width: this.boardButtons[1].width, height: this.boardButtons[1].height }
		let sourceButtonRatio = sourceButtonDimensions.width / sourceButtonDimensions.height

		let maxHeight = viewportHeight * Constants.BOARD.MAX_ALLOWED_BOARD_HEIGHT * (1 / 5)
		let maxWidth = maxHeight * sourceButtonRatio

		let availableWidth = viewportWidth * (1 - 2 * Constants.BOARD.RELATIVE_SIDE_MARGIN);
		let buttonWidthsSum = (14 + 11 * Constants.BOARD.RELATIVE_SPACING_H + 2 * Constants.BOARD.RELATIVE_SPACING_ZERO)

		let buttonW = availableWidth / buttonWidthsSum

		buttonW = Math.min(buttonW, maxWidth);
		let buttonH = buttonW / sourceButtonRatio

		this.setSize(buttonW * buttonWidthsSum, buttonH * (5 + 3 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW))

		let scale = buttonW / sourceButtonDimensions.width;

		let offsetFromZero = Constants.BOARD.RELATIVE_SPACING_ZERO * buttonW

		this.y = (viewportHeight - this.height);


		if (Client.logic.IsDoubleZero()) {
			this.boardButtons[37].setPosition(0);
			this.boardButtons[37].setScale(scale * (this.boardButtons[37].width / this.boardButtons[37].height) * ((1.5 + Constants.BOARD.RELATIVE_SPACING_H * 0.5) / sourceButtonRatio));

			this.boardButtons[0].setPosition(0, this.boardButtonsDictionary[37].y + this.boardButtonsDictionary[37].displayHeight + buttonH * Constants.BOARD.RELATIVE_SPACING_H);
			this.boardButtons[0].setScale(scale * (this.boardButtons[0].width / this.boardButtons[0].height) * ((1.5 + Constants.BOARD.RELATIVE_SPACING_H * 0.5) / sourceButtonRatio));
		} else {
			this.boardButtons[0].setPosition(0)
			this.boardButtons[0].setScale(scale * (this.boardButtons[0].width / this.boardButtons[0].height) * ((3 + Constants.BOARD.RELATIVE_SPACING_H * 2) / sourceButtonRatio))
		}

		for (let i = 0; i < 12; i++) {
			let x = buttonW + offsetFromZero + buttonW * (i * (1 + Constants.BOARD.RELATIVE_SPACING_W))
			for (let j = 3; j > 0; j--) {
				let y = (3 - j) * buttonH * (1 + Constants.BOARD.RELATIVE_SPACING_H)
				this.boardButtons[i * 3 + j].setPosition(x, y)
				this.boardButtons[i * 3 + j].setScale(scale)
			}
		}
		for (let i = 12 * 3; i < 13 * 3; i++) {
			let idxInArray = Client.logic.DoubleZero ? (i + 2) : (i + 1);
			let x = buttonW + offsetFromZero + ((1 + Constants.BOARD.RELATIVE_SPACING_W) * (i - 12 * 3) * 4 * buttonW)
			let y = buttonH * (3 + 2 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW)
			this.boardButtons[idxInArray].setPosition(x, y)
			this.boardButtons[idxInArray].setScale(scale)
		}
		for (let i = 13 * 3; i < 15 * 3; i++) {
			let idxInArray = Client.logic.DoubleZero ? (i + 2) : (i + 1);
			let x = buttonW + offsetFromZero + ((1 + Constants.BOARD.RELATIVE_SPACING_W) * (i - 13 * 3) * 2 * buttonW)
			let y = buttonH * (4 + 3 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW)
			this.boardButtons[idxInArray].setPosition(x, y)
			this.boardButtons[idxInArray].setScale(scale)
		}
		for (let i = 15 * 3; i < 16 * 3; i++) {
			let idxInArray = Client.logic.DoubleZero ? (i + 2) : (i + 1);
			let x = buttonW + (offsetFromZero * 2) + (buttonW * (12 + 11 * Constants.BOARD.RELATIVE_SPACING_W))
			let y = (i - 15 * 3) * buttonH * (1 + Constants.BOARD.RELATIVE_SPACING_H)
			this.boardButtons[idxInArray].setPosition(x, y)
			this.boardButtons[idxInArray].setScale(scale)
		}

		this.repeatBtn.setPosition(0, buttonH * (3 + 2 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW))
		this.repeatBtn.setScale(scale)
		this.repeatBtn.AlignText(0.515, 0.7)

		this.switchLayoutBtn.setPosition(buttonW + (offsetFromZero * 2) + (buttonW * (12 + 11 * Constants.BOARD.RELATIVE_SPACING_W)), buttonH * (3 + 2 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW));
		this.switchLayoutBtn.setScale(scale);
		(this.switchLayoutBtn as MiscButton).AlignText(0.5, 0.7);

		this.presetBtn.setPosition(buttonW + (offsetFromZero * 2) + (buttonW * (12 + 11 * Constants.BOARD.RELATIVE_SPACING_W)), buttonH * (4 + 3 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW))
		this.presetBtn.setScale(scale)
		this.presetBtn.AlignText()

		this.undoBtn.setPosition(0, buttonH * (4 + 3 * Constants.BOARD.RELATIVE_SPACING_H + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW))
		this.undoBtn.setScale(scale)
		this.undoBtn.AlignText(0.5, 0.7)

		this.positionHotSpots()

	}

	protected createHotSpots(): void {
		this.hotSpotsTable = new Array(28)
		for (let x = 0; x < this.hotSpotsTable.length; x++) {
			this.hotSpotsTable[x] = new Array(11);
		}

		let boardHostspotDimensions = [[0, 0], [50, 0], [50, 50], [0, 50]];

		let arr5: Array<BET_CHECKING_MODE> = new Array();
		arr5.push(BET_CHECKING_MODE.TOP)
		for (let x = 1; x <= 24; x++) {
			for (let y = 0; y <= 6; y++) {
				if (y != 1)
					if (x == 24)
						this.hotSpotsTable[x][y] = new HotSpot(this.scene, x, y, boardHostspotDimensions, -15, 0);
					else
						this.hotSpotsTable[x][y] = new HotSpot(this.scene, x, y, boardHostspotDimensions);
				else
					this.hotSpotsTable[x][y] = new HotSpot(this.scene, x, y, boardHostspotDimensions, (x == 24 ? -15 : 0), 15, false, true, arr5); //y == 1 

				this.add(this.hotSpotsTable[x][y])
			}
		}
		/* Zero                                                                  	*/
		this.hotSpotsTable[0][3] = new HotSpot(this.scene, 0, 3, boardHostspotDimensions, 10, 0, false, true, new Array(BET_CHECKING_MODE.TOP, BET_CHECKING_MODE.LEFT, BET_CHECKING_MODE.BOTTOM))
		this.add(this.hotSpotsTable[0][3])
		this.hotSpotsTable[0][3].AddButton([this.boardButtonsDictionary["0"]]) //must be [] even if only 1 element

		/* DoubleZero HotSpot */
		if (Client.logic.IsDoubleZero()) {
			/**
			 * FOR REFERENCE X, Y coordinates on double zero game
			 */
			// let doubleZeroX = 0
			// let doubleZeroY = 1
			this.hotSpotsTable[0][1] = new HotSpot(this.scene, 0, 1, boardHostspotDimensions, 10, 0, false, true, new Array(BET_CHECKING_MODE.TOP, BET_CHECKING_MODE.LEFT, BET_CHECKING_MODE.BOTTOM))
			this.add(this.hotSpotsTable[0][1])
			this.hotSpotsTable[0][1].AddButton([this.boardButtonsDictionary["37"]])
		}

		/* 1st 12                                                                   */
		let arr: Array<BET_CHECKING_MODE> = new Array();
		arr.push(BET_CHECKING_MODE.LEFT)
		this.hotSpotsTable[5][8] = new HotSpot(this.scene, 5, 8, boardHostspotDimensions, 0, 0, false, true, arr);
		this.add(this.hotSpotsTable[5][8]);
		this.hotSpotsTable[5][8].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_THIRD],
		this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"], this.boardButtonsDictionary["4"],
		this.boardButtonsDictionary["5"], this.boardButtonsDictionary["6"], this.boardButtonsDictionary["7"], this.boardButtonsDictionary["8"],
		this.boardButtonsDictionary["9"], this.boardButtonsDictionary["10"], this.boardButtonsDictionary["11"], this.boardButtonsDictionary["12"]]);


		/*2nd 12;                                                                   */
		this.hotSpotsTable[13][8] = new HotSpot(this.scene, 13, 8, boardHostspotDimensions)
		this.add(this.hotSpotsTable[13][8])
		this.hotSpotsTable[13][8].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_THIRD],
		this.boardButtonsDictionary["13"], this.boardButtonsDictionary["14"], this.boardButtonsDictionary["15"], this.boardButtonsDictionary["16"],
		this.boardButtonsDictionary["17"], this.boardButtonsDictionary["18"], this.boardButtonsDictionary["19"], this.boardButtonsDictionary["20"],
		this.boardButtonsDictionary["21"], this.boardButtonsDictionary["22"], this.boardButtonsDictionary["23"], this.boardButtonsDictionary["24"]])

		/*3rd 12;                                                                   */
		let arr2: Array<BET_CHECKING_MODE> = new Array();
		arr2.push(BET_CHECKING_MODE.RIGHT)
		this.hotSpotsTable[21][8] = new HotSpot(this.scene, 21, 8, boardHostspotDimensions, 0, 0, false, true, arr2)
		this.add(this.hotSpotsTable[21][8])
		this.hotSpotsTable[21][8].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.THIRD_THIRD],
		this.boardButtonsDictionary["25"], this.boardButtonsDictionary["26"], this.boardButtonsDictionary["27"], this.boardButtonsDictionary["28"],
		this.boardButtonsDictionary["29"], this.boardButtonsDictionary["30"], this.boardButtonsDictionary["31"], this.boardButtonsDictionary["32"],
		this.boardButtonsDictionary["33"], this.boardButtonsDictionary["34"], this.boardButtonsDictionary["35"], this.boardButtonsDictionary["36"]])

		/*1to 18;                                                                   */
		this.hotSpotsTable[3][7] = new HotSpot(this.scene, 3, 7, boardHostspotDimensions, 0, 0, false, true, new Array(BET_CHECKING_MODE.LEFT, BET_CHECKING_MODE.BOTTOM))
		this.add(this.hotSpotsTable[3][7])
		this.hotSpotsTable[3][7].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_HALF],
		this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"], this.boardButtonsDictionary["4"],
		this.boardButtonsDictionary["5"], this.boardButtonsDictionary["6"], this.boardButtonsDictionary["7"], this.boardButtonsDictionary["8"],
		this.boardButtonsDictionary["9"], this.boardButtonsDictionary["10"], this.boardButtonsDictionary["11"], this.boardButtonsDictionary["12"],
		this.boardButtonsDictionary["13"], this.boardButtonsDictionary["14"], this.boardButtonsDictionary["15"], this.boardButtonsDictionary["16"],
		this.boardButtonsDictionary["17"], this.boardButtonsDictionary["18"]])

		/*Even                                                                      */
		let arr4: Array<BET_CHECKING_MODE> = new Array();
		arr4.push(BET_CHECKING_MODE.BOTTOM)
		this.hotSpotsTable[7][7] = new HotSpot(this.scene, 7, 7, boardHostspotDimensions, 0, 0, false, true, arr4)
		this.add(this.hotSpotsTable[7][7])
		this.hotSpotsTable[7][7].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.EVEN],
		this.boardButtonsDictionary["2"], this.boardButtonsDictionary["4"], this.boardButtonsDictionary["6"], this.boardButtonsDictionary["8"],
		this.boardButtonsDictionary["10"], this.boardButtonsDictionary["12"], this.boardButtonsDictionary["14"], this.boardButtonsDictionary["16"],
		this.boardButtonsDictionary["18"], this.boardButtonsDictionary["20"], this.boardButtonsDictionary["22"], this.boardButtonsDictionary["24"],
		this.boardButtonsDictionary["26"], this.boardButtonsDictionary["28"], this.boardButtonsDictionary["30"], this.boardButtonsDictionary["32"],
		this.boardButtonsDictionary["34"], this.boardButtonsDictionary["36"]])

		// Red
		this.hotSpotsTable[15][7] = new HotSpot(this.scene, 15, 7, boardHostspotDimensions, 0, 0, false, true, arr4)
		this.add(this.hotSpotsTable[15][7])
		this.hotSpotsTable[15][7].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.RED],
		this.boardButtonsDictionary["1"], this.boardButtonsDictionary["3"], this.boardButtonsDictionary["5"], this.boardButtonsDictionary["7"],
		this.boardButtonsDictionary["9"], this.boardButtonsDictionary["12"], this.boardButtonsDictionary["14"], this.boardButtonsDictionary["16"],
		this.boardButtonsDictionary["18"], this.boardButtonsDictionary["19"], this.boardButtonsDictionary["21"], this.boardButtonsDictionary["23"],
		this.boardButtonsDictionary["25"], this.boardButtonsDictionary["27"], this.boardButtonsDictionary["30"], this.boardButtonsDictionary["32"],
		this.boardButtonsDictionary["34"], this.boardButtonsDictionary["36"]])

		/*Black                                                                     */
		this.hotSpotsTable[11][7] = new HotSpot(this.scene, 11, 7, boardHostspotDimensions, 0, 0, false, true, arr4)
		this.add(this.hotSpotsTable[11][7])
		this.hotSpotsTable[11][7].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.BLACK],
		this.boardButtonsDictionary["2"], this.boardButtonsDictionary["4"], this.boardButtonsDictionary["6"], this.boardButtonsDictionary["8"],
		this.boardButtonsDictionary["10"], this.boardButtonsDictionary["11"], this.boardButtonsDictionary["13"], this.boardButtonsDictionary["15"],
		this.boardButtonsDictionary["17"], this.boardButtonsDictionary["20"], this.boardButtonsDictionary["22"], this.boardButtonsDictionary["24"],
		this.boardButtonsDictionary["26"], this.boardButtonsDictionary["28"], this.boardButtonsDictionary["29"], this.boardButtonsDictionary["31"],
		this.boardButtonsDictionary["33"], this.boardButtonsDictionary["35"]])

		/*Odd                                                                       */
		this.hotSpotsTable[19][7] = new HotSpot(this.scene, 19, 7, boardHostspotDimensions, 0, 0, false, true, arr4)
		this.add(this.hotSpotsTable[19][7])
		this.hotSpotsTable[19][7].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.ODD],
		this.boardButtonsDictionary["1"], this.boardButtonsDictionary["3"], this.boardButtonsDictionary["5"], this.boardButtonsDictionary["7"],
		this.boardButtonsDictionary["9"], this.boardButtonsDictionary["11"], this.boardButtonsDictionary["13"], this.boardButtonsDictionary["15"],
		this.boardButtonsDictionary["17"], this.boardButtonsDictionary["19"], this.boardButtonsDictionary["21"], this.boardButtonsDictionary["23"],
		this.boardButtonsDictionary["25"], this.boardButtonsDictionary["27"], this.boardButtonsDictionary["29"], this.boardButtonsDictionary["31"],
		this.boardButtonsDictionary["33"], this.boardButtonsDictionary["35"]])

		/*19 to 36                                                                  */
		this.hotSpotsTable[23][7] = new HotSpot(this.scene, 23, 7, boardHostspotDimensions, 0, 0, false, true, new Array(BET_CHECKING_MODE.RIGHT, BET_CHECKING_MODE.BOTTOM))
		this.add(this.hotSpotsTable[23][7])
		this.hotSpotsTable[23][7].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_HALF],
		this.boardButtonsDictionary["19"], this.boardButtonsDictionary["20"], this.boardButtonsDictionary["21"], this.boardButtonsDictionary["22"],
		this.boardButtonsDictionary["23"], this.boardButtonsDictionary["24"], this.boardButtonsDictionary["25"], this.boardButtonsDictionary["26"],
		this.boardButtonsDictionary["27"], this.boardButtonsDictionary["28"], this.boardButtonsDictionary["29"], this.boardButtonsDictionary["30"],
		this.boardButtonsDictionary["31"], this.boardButtonsDictionary["32"], this.boardButtonsDictionary["33"], this.boardButtonsDictionary["34"],
		this.boardButtonsDictionary["35"], this.boardButtonsDictionary["36"]])


		/*top 2 to 1                                                                */
		this.hotSpotsTable[25][1] = new HotSpot(this.scene, 25, 1, boardHostspotDimensions, 0, 0, false, true, new Array(BET_CHECKING_MODE.TOP, BET_CHECKING_MODE.RIGHT))
		this.add(this.hotSpotsTable[25][1])
		this.hotSpotsTable[25][1].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.TWELVE_ONE],
		this.boardButtonsDictionary["3"], this.boardButtonsDictionary["6"], this.boardButtonsDictionary["9"], this.boardButtonsDictionary["12"],
		this.boardButtonsDictionary["15"], this.boardButtonsDictionary["18"], this.boardButtonsDictionary["21"], this.boardButtonsDictionary["24"],
		this.boardButtonsDictionary["27"], this.boardButtonsDictionary["30"], this.boardButtonsDictionary["33"], this.boardButtonsDictionary["36"]])

		/*center 2 to 1																*/
		let arr3: Array<BET_CHECKING_MODE> = new Array();
		arr3.push(BET_CHECKING_MODE.RIGHT)
		this.hotSpotsTable[25][3] = new HotSpot(this.scene, 25, 3, boardHostspotDimensions, 0, 0, false, true, arr3);
		this.add(this.hotSpotsTable[25][3])
		this.hotSpotsTable[25][3].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.TWENTY_ONE_ONE],
		this.boardButtonsDictionary["2"], this.boardButtonsDictionary["5"], this.boardButtonsDictionary["8"], this.boardButtonsDictionary["11"],
		this.boardButtonsDictionary["14"], this.boardButtonsDictionary["17"], this.boardButtonsDictionary["20"], this.boardButtonsDictionary["23"],
		this.boardButtonsDictionary["26"], this.boardButtonsDictionary["29"], this.boardButtonsDictionary["32"], this.boardButtonsDictionary["35"]])

		/*bottom 2 to 1																*/
		this.hotSpotsTable[25][5] = new HotSpot(this.scene, 25, 5, boardHostspotDimensions, 0, 0, false, true, new Array(BET_CHECKING_MODE.BOTTOM, BET_CHECKING_MODE.RIGHT))
		this.add(this.hotSpotsTable[25][5])
		this.hotSpotsTable[25][5].AddButton([this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.THIRTY_TWO_ONE],
		this.boardButtonsDictionary["1"], this.boardButtonsDictionary["4"], this.boardButtonsDictionary["7"], this.boardButtonsDictionary["10"],
		this.boardButtonsDictionary["13"], this.boardButtonsDictionary["16"], this.boardButtonsDictionary["19"], this.boardButtonsDictionary["22"],
		this.boardButtonsDictionary["25"], this.boardButtonsDictionary["28"], this.boardButtonsDictionary["31"], this.boardButtonsDictionary["34"]])

		/*stolpec 1 NULA 															*/
		if (Client.logic.IsDoubleZero()) {
			this.hotSpotsTable[1][0].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["37"], this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"]])
			this.hotSpotsTable[1][1].AddButton([this.boardButtonsDictionary["37"], this.boardButtonsDictionary["3"]])
			this.hotSpotsTable[1][2].AddButton([this.boardButtonsDictionary["37"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"]])
			this.hotSpotsTable[1][3].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["37"], this.boardButtonsDictionary["2"]])
			this.hotSpotsTable[1][4].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"]])
			this.hotSpotsTable[1][5].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["1"]])
			this.hotSpotsTable[1][6].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["37"], this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"]])
		} else {
			this.hotSpotsTable[1][0].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"]])
			this.hotSpotsTable[1][1].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["3"]])
			this.hotSpotsTable[1][2].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"]])
			this.hotSpotsTable[1][3].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["2"]])
			this.hotSpotsTable[1][4].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"]])
			this.hotSpotsTable[1][5].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["1"]])
			this.hotSpotsTable[1][6].AddButton([this.boardButtonsDictionary["0"], this.boardButtonsDictionary["1"], this.boardButtonsDictionary["2"], this.boardButtonsDictionary["3"]])
		}

		let pass = 0;
		for (let i = 2; i <= 24; i += 2) {
			/*vrstica 2 */
			this.hotSpotsTable[i][0].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)], this.boardButtonsDictionary[3 + (pass * 3)]])
			this.hotSpotsTable[i][1].AddButton([this.boardButtonsDictionary[3 + (pass * 3)]])
			this.hotSpotsTable[i][2].AddButton([this.boardButtonsDictionary[2 + (pass * 3)], this.boardButtonsDictionary[3 + (pass * 3)]])
			this.hotSpotsTable[i][3].AddButton([this.boardButtonsDictionary[2 + (pass * 3)]])
			this.hotSpotsTable[i][4].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)]])
			this.hotSpotsTable[i][5].AddButton([this.boardButtonsDictionary[1 + (pass * 3)]])
			this.hotSpotsTable[i][6].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)], this.boardButtonsDictionary[3 + (pass * 3)]])

			pass++;
		}

		pass = 0;
		for (let i = 3; i <= 23; i += 2) {
			/*vrstica 2 */
			this.hotSpotsTable[i][0].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)], this.boardButtonsDictionary[3 + (pass * 3)],
			this.boardButtonsDictionary[1 + ((pass + 1) * 3)], this.boardButtonsDictionary[2 + ((pass + 1) * 3)], this.boardButtonsDictionary[3 + ((pass + 1) * 3)]])

			this.hotSpotsTable[i][1].AddButton([this.boardButtonsDictionary[3 + (pass * 3)], this.boardButtonsDictionary[3 + ((pass + 1) * 3)]])

			this.hotSpotsTable[i][2].AddButton([this.boardButtonsDictionary[3 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)],
			this.boardButtonsDictionary[3 + ((pass + 1) * 3)], this.boardButtonsDictionary[2 + ((pass + 1) * 3)]])

			this.hotSpotsTable[i][3].AddButton([this.boardButtonsDictionary[2 + (pass * 3)], this.boardButtonsDictionary[2 + ((pass + 1) * 3)]])

			this.hotSpotsTable[i][4].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)],
			this.boardButtonsDictionary[1 + ((pass + 1) * 3)], this.boardButtonsDictionary[2 + ((pass + 1) * 3)]])

			this.hotSpotsTable[i][5].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[1 + ((pass + 1) * 3)]])

			this.hotSpotsTable[i][6].AddButton([this.boardButtonsDictionary[1 + (pass * 3)], this.boardButtonsDictionary[2 + (pass * 3)], this.boardButtonsDictionary[3 + (pass * 3)],
			this.boardButtonsDictionary[1 + ((pass + 1) * 3)], this.boardButtonsDictionary[2 + ((pass + 1) * 3)], this.boardButtonsDictionary[3 + ((pass + 1) * 3)]])

			pass++;
		}
	}

	protected positionHotSpots(): void {
		this.hotSpotsTable.forEach((element, x) => {
			element.forEach((hotspot, y) => {
				let newPosition = [0, 0]
				let positions = [Array(), Array()]
				let size = [hotspot.ButtonsList[0].displayWidth, hotspot.ButtonsList[0].displayHeight]
				hotspot.ButtonsList.forEach(button => {
					size[0] = Math.min(button.displayWidth, size[0])
					size[1] = Math.min(button.displayHeight, size[1])
					positions[0].push(button.x + button.displayWidth / 2)
					positions[1].push(button.y + button.displayHeight / 2)
				});
				positions.forEach((array, i) => {
					array.sort((a: number, b: number) => a - b)
					if (array.length % 2 == 0) {
						let index = Math.floor((array.length - 1) / 2)
						newPosition[i] = (array[index] + array[index + 1]) / 2
					}
					else
						newPosition[i] = array[(array.length - 1) / 2]
				});
				if (y == 0)
					newPosition[1] = 0
				else if (y == 6)
					newPosition[1] = size[1] * (3 + 2.5 * Constants.BOARD.RELATIVE_SPACING_H)
				else if (y >= 7) {
					newPosition[0] = positions[0][0] - size[0] / 2
					newPosition[1] = size[1] * ((3 + 8 - y) + Constants.BOARD.RELATIVE_SPACING_H * (10 - y) + Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW)
					if (y == 7) {
						let adjustedX = x
						if (adjustedX == 15) adjustedX = 11
						else if (adjustedX == 11) adjustedX = 15
						newPosition[0] = size[0] * (1 + Constants.BOARD.RELATIVE_SPACING_ZERO) + (adjustedX - 3) / 4 * size[0] * 2 * (1 + Constants.BOARD.RELATIVE_SPACING_W)
					}
				}
				if (x == 1) {
					newPosition[0] = size[0] * (1 + Constants.BOARD.RELATIVE_SPACING_ZERO - Constants.BOARD.RELATIVE_SPACING_W)
					newPosition[1] = size[1] * (y / 2 + (y - 1) * 0.5 * Constants.BOARD.RELATIVE_SPACING_H)
				} else if (x == 25)
					newPosition[0] = positions[0][positions[0].length - 1]
				if (x == 0) {
					if (!Client.logic.IsDoubleZero()) {
						newPosition[0] = 0
						newPosition[1] = 0
						hotspot.setDisplaySize(size[0] * (1 + Constants.BOARD.RELATIVE_SPACING_ZERO / 2 - 0.25), size[1])
					} else {
						if (y == 1) {
							newPosition[0] = 0
							newPosition[1] = 0
							hotspot.setDisplaySize(size[0] * (1 + Constants.BOARD.RELATIVE_SPACING_ZERO / 2 - 0.25), size[1])
						} else {
							newPosition[0] = 0
							newPosition[1] = size[1]
							hotspot.setDisplaySize(size[0] * (1 + Constants.BOARD.RELATIVE_SPACING_ZERO / 2 - 0.25), size[1])
						}
					}
				} else if (y == 7)
					hotspot.setDisplaySize(size[0] * (2 + 1 * Constants.BOARD.RELATIVE_SPACING_W), size[1])
				else if (y == 8) {
					let offset = size[1] * (0.25 - Constants.BOARD.RELATIVE_SPACING_NUMBERS_ROW)
					hotspot.setDisplaySize(size[0] * (4 + 3 * Constants.BOARD.RELATIVE_SPACING_W), size[1] - offset)
					newPosition[1] += offset
				} else if (x == 25) {
					hotspot.setDisplaySize(size[0], size[1])
					newPosition[0] -= hotspot.displayWidth / 2
					newPosition[1] -= hotspot.displayHeight / 2
				}
				else {
					let hotspotWidth = size[0] * ((x % 2) ? (0.5 + Constants.BOARD.RELATIVE_SPACING_W) : 0.5)
					let hotspotHeight = size[1] * ((y % 2 || y == 0) ? 0.5 : (0.5 + Constants.BOARD.RELATIVE_SPACING_H))
					if (x == 1) hotspotWidth = size[0] * (0.5 + Constants.BOARD.RELATIVE_SPACING_ZERO)
					if (x == 24) hotspotWidth = size[0] * 0.75
					if (y == 1) hotspotHeight = size[1] * 0.75
					hotspot.setDisplaySize(hotspotWidth, hotspotHeight)
					newPosition[0] -= hotspotWidth / 2
					newPosition[1] -= hotspotHeight / 2
					if (x == 24) newPosition[0] += 0.125 * size[0]
					if (y == 1) newPosition[1] -= 0.125 * size[1]
				}
				hotspot.setPosition(...newPosition)
				if (y == 0) hotspot.setVisible(false)
			});
		});
	}

	protected enableHotspots(): void {
		this.hotSpotsTable.forEach(element => {
			element.forEach(element => {
				element.setInteractive()
			});
		});
	}

	protected disableHotspots(): void {
		this.hotSpotsTable.forEach(element => {
			element.forEach(element => {
				element.disableInteractive()
			});
		});
	}

	protected displayWins(winNumber: number): void {
		winNumber = parseInt(winNumber as any) // .indexOf won't work properly if string
		this.boardButtons.forEach(element => {
			element.SetNormal()
		});

		if (winNumber == -1) {
			this.PlayWinAnimation(null, winNumber);
			return;
		}

		// Add this function to update when State == Winning to keep the button at winning state because when the player hovers, it changes the board button state    
		let winningButton = this.boardButtonsDictionary[winNumber.toString()]
		winningButton.SetWinning()
		if (Client.logic.IsBoard) this.betsDisplay.SetWinPosition(new Phaser.Math.Vector2(winningButton.x + winningButton.displayWidth / 2, winningButton.y + winningButton.displayHeight / 2))

		if (winNumber >= 1 && winNumber <= 12) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_THIRD].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_THIRD].SetNormal()
		if (winNumber >= 13 && winNumber <= 24) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_THIRD].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_THIRD].SetNormal()
		if (winNumber >= 25 && winNumber <= 36) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.THIRD_THIRD].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.THIRD_THIRD].SetNormal()

		if (winNumber >= 1 && winNumber <= 18) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_HALF].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.FIRST_HALF].SetNormal()
		if (winNumber >= 19 && winNumber <= 36) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_HALF].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.SECOND_HALF].SetNormal()

		if (winNumber % 2 == 0 && winNumber != 0) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.EVEN].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.EVEN].SetNormal()
		if (winNumber % 2 && winNumber != 37) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.ODD].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.ODD].SetNormal()

		let c = numToColor(winNumber)
		if (c == Constants.COLORS.BLACK) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.BLACK].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.BLACK].SetNormal()
		if (c == Constants.COLORS.RED) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.RED].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.RED].SetNormal()

		if (winNumber % 3 == 0 && winNumber != 0) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.TWELVE_ONE].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.TWELVE_ONE].SetNormal();
		let numbers_22to1 = [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35]
		if (numbers_22to1.indexOf(winNumber) !== -1) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.TWENTY_ONE_ONE].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.TWENTY_ONE_ONE].SetNormal();
		let numbers_32to1 = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34]
		if (numbers_32to1.indexOf(winNumber) !== -1) this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.THIRTY_TWO_ONE].SetWinning(); else this.boardButtonsDictionary[Constants.BOARD.BOARD_BUTTON_NAMES.THIRTY_TWO_ONE].SetNormal();

		this.PlayWinAnimation(winningButton, winNumber);
	}

	/**********************************STATISTICS**********************************/
	public toggleNumberStatistics(updatePositionsOnly: boolean = false): void {//toggle statistics overlay on		
		if (Client.logic.CurrentHistoryNumbers.length <= 0)
			return;

		if (!this.statisticsAreDisplayed || updatePositionsOnly) {
			this.statisticsAreDisplayed = true;

			if (!this.numberStatistics && !updatePositionsOnly)
				this.updateStatistics(true);

			if (!updatePositionsOnly)
				this.statisticsBars = new Array(this.numberStatistics.length);

			this.numberStatistics.forEach((mn, i) => {
				let percentage = mn.count / Client.logic.CurrentHistoryNumbers.length;

				if (!this.statisticsBars[i] && !updatePositionsOnly) {
					this.statisticsBars[mn.num] = new StatsHolder(this.scene);
					this.add(this.statisticsBars[mn.num]);
					this.statisticsBars[mn.num].setPosition(this.boardButtons[mn.num].x, this.boardButtons[mn.num].y);
					this.statisticsBars[mn.num].UpdateValues(this.boardButtons[i].displayWidth, this.boardButtons[i].displayHeight, mn.num, percentage, Client.logic.currentGameState.HotNumbers.includes(i), Client.logic.currentGameState.ColdNumbers.includes(i));
				}
				else {
					this.statisticsBars[mn.num].setPosition(this.boardButtons[mn.num].x, this.boardButtons[mn.num].y);
					this.statisticsBars[mn.num].UpdateValues(this.boardButtons[i].displayWidth, this.boardButtons[i].displayHeight, mn.num, percentage, Client.logic.currentGameState.HotNumbers.includes(i), Client.logic.currentGameState.ColdNumbers.includes(i));
				}
				this.statisticsBars[mn.num].visible = true;
				this.statisticsBars[mn.num].active = true;
			})

			this.bringToTop(this.betsDisplay.ChipContainer);
		}
		else {
			this.statisticsAreDisplayed = false;

			this.statisticsBars.forEach(st => {
				st.visible = false;
				st.active = false;
				/*
				st.DestroyAll();
				st.destroy();
				st = null;
				this.remove(st, true);
				*/
			})

			this.statisticsBars.splice(0, this.statisticsBars.length);
		}
	}

	protected updateStatistics(selfUpdate: boolean = false): void {//call at start of every bets open and first toggle of statistics
		if (Client.logic.CurrentHistoryNumbers.length <= 0)
			return;

		this.numberStatistics = Client.logic.CalculateWinNumberStatistics();

		if (this.statisticsAreDisplayed && !selfUpdate) {//update statistics if visible
			this.toggleNumberStatistics(true);
		}
	}
	/******************************************************************************/


	OnPhaseEvent(phase: number): void {
		//disabling glows on all hotspots on bets are closed phase.
		if (phase == Phase.BALL_OUT_STATE || phase == Phase.WAITING_WIN_NUMBER_STATE) {
			this.hotSpotsTable.forEach(element => {
				element.forEach(element2 => {
					element2.SetButtonsNormal();
				});
			});
		};

		if (phase == Phase.BETS_OPEN_STATE) {
			console.log("Updated statistics.");
			this.updateStatistics();
		}
	}

	ToggleHotSpotsEffects(enabled: boolean) {
		this.hotSpotsTable.forEach(element => {
			element.forEach(element2 => {
				element2.ToggleEffectsEnabled(enabled);
			});
		});
	}

	OnResize(width: number, height: number, portraitMode, staticW, staticH): void {
		if (this.winAnimation)
			this.winAnimation.forEach(tween => {
				tween.stop();
			});

		if (this.repeatBtn)
			this.repeatBtn.TextElement.text = this.isBoardEmpty ? Language.Get(Messages.REPEAT) : Language.Get(Messages.DOUBLE);

		this.positionBoardButtons(width, height, portraitMode, staticW, staticH);
		this.inputRect.setSize(this.width, this.height);
		this.bringToTop(this.inputRect);
		this.inputRect.removeInteractive();
		this.inputRect.setInteractive();
		this.x = (width - this.width) / 2;
		this.betsDisplay.RefreshChips();
	}

	protected OnEmptyChanged(value: boolean) {
		this.isBoardEmpty = value;
		if (this.repeatBtn) {
			this.repeatBtn.TextElement.text = this.isBoardEmpty ? Language.Get(Messages.REPEAT) : Language.Get(Messages.DOUBLE);
			this.repeatBtn.AlignText(0.515, 0.7);
		}
	}

	ToggleButtonsHighLight(hotSpots: Array<number>, highlighted: boolean) {
		if (!hotSpots) {
			this.hotSpotsTable.forEach(element => {
				element.forEach(element2 => {
					highlighted ? element2.SetButtonsHighlighted() : element2.SetButtonsNormal();
				})
			})
		} else {
			hotSpots.forEach(element => {
				(highlighted) ? this.boardButtonsDictionary[element.toString()].SetWinning() : this.boardButtonsDictionary[element.toString()].SetNormal();
			});
		}
	}

	DoLinearInterpolation(_x: number, _y: number, ignoreHotspot: HotSpot): void {
		//pogoj samo če so neighbourji
		if (Math.abs(this.betsDisplay.LastIgnoredHotspot.hotSpotBetY - ignoreHotspot.hotSpotBetY) + Math.abs(this.betsDisplay.LastIgnoredHotspot.hotSpotBetX - ignoreHotspot.hotSpotBetX) <= 1) {
			this.betsDisplay.SetStartingTouchPosition(_x, _y, ignoreHotspot);
			return;
		}

		let vh: number = this.scene.scale.height;
		let dX: number = (_x - this.betsDisplay.TouchStartX);
		let dY: number = -(_y - this.betsDisplay.TouchStartY);
		let sX: number = this.betsDisplay.TouchStartX;
		let sY: number = vh - this.betsDisplay.TouchStartY;

		for (let j = 0; j < this.hotSpotsTable.length; j++) {
			for (let k = 0; k < this.hotSpotsTable[j].length; k++) {

				if (!this.hotSpotsTable[j][k] || ignoreHotspot == this.hotSpotsTable[j][k] || this.hotSpotsTable[j][k] == this.betsDisplay.LastIgnoredHotspot) continue;

				let bounds: Phaser.Geom.Rectangle = this.hotSpotsTable[j][k].getBounds();
				bounds.y = vh - (bounds.y + bounds.height);

				let D: number = bounds.width * dY - bounds.height * dX;
				let s: number = (bounds.width * (bounds.y - sY) + bounds.height * (sX - bounds.x)) / D;
				let t: number = (sX + (s * dX) - bounds.x) / bounds.width;

				bounds.y += bounds.height;
				bounds.height = -bounds.height;

				let D2: number = bounds.width * dY - bounds.height * dX;
				let s2: number = (bounds.width * (bounds.y - sY) + bounds.height * (sX - bounds.x)) / D2;
				let t2: number = (sX + (s2 * dX) - bounds.x) / bounds.width;
				let intersetcWithLine1: boolean = ((s >= 0) && (s <= 1)) && ((t >= 0) && (t <= 1));
				let intersetcWithLine2: boolean = ((s2 >= 0) && (s2 <= 1)) && ((t2 >= 0) && (t2 <= 1));

				if (intersetcWithLine1 || intersetcWithLine2) {
					//je znotraj, postavi chip na ta betfield
					this.hotSpotsTable[j][k].touchActive = false;
					Client.logic.PlaceBet(j, k);
				}
			}
		}
		this.betsDisplay.SetStartingTouchPosition(_x, _y, ignoreHotspot);
	}

	protected PlayWinAnimation(winningButton?, winNumber?): void {
		if (winNumber == -1 || !winningButton)
			return;

		//---Board explosion ripple animation
		if (!Client.logic.historyShown) {
			let maxJumpSize = this.displayHeight / 20;
			let maxDistanceAway = this.displayWidth * 0.8;
			let originalDisplayWidth = winningButton.displayWidth;

			this.boardButtonsDictionary.forEach(boardButton => {
				let intensityFactor = Math.min(1, Math.max(0, 1 - (Phaser.Math.Distance.BetweenPoints(new Phaser.Math.Vector2(winningButton.x, winningButton.y), new Phaser.Math.Vector2(boardButton.x, boardButton.y)) / maxDistanceAway)));
				let maxExplodeScale = Math.max(boardButton.scale * 1.05, (boardButton.scale * 1.25) * intensityFactor);
				let originalScale = boardButton.scale;
				if (boardButton != winningButton)
					this.sendToBack(boardButton);

				this.winAnimation.push(this.scene.tweens.add({
					targets: [boardButton],
					scale: { from: boardButton.scale, to: maxExplodeScale },
					y: { from: boardButton.y, to: boardButton.y - (maxJumpSize * intensityFactor) },
					x: { from: boardButton.x, to: boardButton.x - (originalDisplayWidth * (maxExplodeScale - originalScale)) / 2 },
					ease: 'Cubic',
					duration: 500 /*+ 200 * intensityFactor*/,
					repeat: 0,
					yoyo: true,
					delay: 1000 * (1 - intensityFactor),
					onComplete: function (tween, targets) {
						this.winAnimation.splice(this.winAnimation.indexOf(tween), 1);
					}.bind(this)
				}));
			});
		}
	}
}

export class ToggleLayout implements ButtonAction {
	constructor() {

	}

	Action(): void {
		Client.logic.ToggleTrackView();
	}
}

export class TogglePresets implements ButtonAction {
	constructor(private scene: Phaser.Scene) {

	}

	Action(): void {
		if (virtualWheel && !Client.logic.BetsAllowed)
			return;
		this.scene.game.events.emit("TOGGLE_PRESETS_VISIBILITY");
		this.scene.game.events.emit(Constants.EVENTS.VIDEO_ZOOM_IN, -1);
		this.scene.sound.play(Constants.SOUNDS.SLIDER_CHANGE, { volume: 0.20 * effectsVolume });
	}
}